/**
 * MapLibre map component — fully declarative via react-map-gl.
 *
 * All map content (base style, per-flight Sources/Layers, point Markers, ScaleControl)
 * is declared as JSX children of <Map>. react-map-gl keeps everything in sync with
 * React state — no imperative map.addLayer/addSource/setStyle/addControl calls remain.
 * Camera moves (easeTo, fitBounds) are the sole imperative operations, via the map ref.
 *
 * @param mapRef          - held by Tracker.tsx for camera operations (MapRef from react-map-gl)
 * @param initialViewport - optional saved viewport from sessionStorage (§23b)
 * @param onStyleLoad     - fired after the initial map load (reserved for future use)
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { MutableRefObject, FC } from 'react';
import {
  Map as RMap,
  Source,
  Layer,
  Marker,
  Popup,
  NavigationControl,
  ScaleControl,
} from 'react-map-gl/maplibre';
import type { MapRef, MapLayerMouseEvent } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { ExpressionSpecification } from 'maplibre-gl';
import type { LucideProps } from 'lucide-react';

import { MAP_STYLES, MAP_STYLE_CYCLE, buildHybridStyle } from '../constants/mapStyles';
import type { MapStyleKey } from '../constants/mapStyles';
import { IconMapping } from '../constants/iconMapping';
import { darkenColor, HEATMAP_CONFIG } from '../constants/colors';
import { MAP_CONFIG } from '../constants/mapConfig';
import { fitToSelection } from '../utils/cameraHelpers';
import { saveViewport } from '../utils/sessionRestore';
import type { MapViewport } from '../utils/sessionRestore';
import {
  buildTrajectoryLine,
  buildTrajectoryPoints,
  buildPolygon,
  buildHeatmap,
  hexToRgba,
  heatmapColorExpr,
} from '../utils/mapLayers';
import { useAppStore, getFlightColor } from '../store/appStore';
import styles from './Map.module.css';

// ── MarkerBadge ───────────────────────────────────────────────────────────────

interface MarkerBadgeProps {
  longitude: number;
  latitude: number;
  anchor: 'center' | 'bottom' | 'top' | 'left' | 'right';
  color: string;
  Icon: FC<LucideProps>;
  /** Badge disc size in px (default 32). */
  size?: number;
  /** Icon size in px (default 16). */
  iconSize?: number;
  /**
   * When true (default): wraps the icon in a filled circular disc of `color`
   * with a white icon. Use for outline/stroke icon libraries like Lucide.
   * When false: renders the icon alone, colored with `color`. Use with icon
   * libraries that provide filled-variant icons natively (Phosphor filled, etc.).
   */
  showBadge?: boolean;
  onClick?: () => void;
}

/**
 * Shared map marker wrapper for all 3 point marker types.
 * All markers use showBadge=true (filled disc) for readability on map tiles.
 * The showBadge=false path is reserved for a future icon-library swap.
 */
function MarkerBadge({
  longitude, latitude, anchor, color, Icon,
  size = 32, iconSize = 16, showBadge = true, onClick,
}: MarkerBadgeProps) {
  const inner = showBadge ? (
    <div
      onClick={onClick}
      style={{
        width: size, height: size, borderRadius: '50%', background: color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
      }}
    >
      <Icon color="white" size={iconSize} />
    </div>
  ) : (
    <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <Icon color={color} size={size} />
    </div>
  );

  return (
    <Marker longitude={longitude} latitude={latitude} anchor={anchor}>
      {inner}
    </Marker>
  );
}

// ── Popup state ───────────────────────────────────────────────────────────────

interface TeamPopup {
  kind: 'team';
  lng: number;
  lat: number;
  terminalName: string;
  teamName: string | null;
  userName: string | null;
  timestamp: string | null;
}

interface LandingPopup {
  kind: 'landing';
  lng: number;
  lat: number;
  predType: string;
  predLat: number;
  predLng: number;
  city: string;
  distKm: number;
  landingTime: string;
}

type PopupInfo = TeamPopup | LandingPopup;

// ── Popup helpers ─────────────────────────────────────────────────────────────

/** Formats an ISO 8601 UTC timestamp as `HH:MM UTC`. */
function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm} UTC`;
}

/** Formats an ISO 8601 UTC timestamp as `YYYY-MM-DD HH:MM:SS` (no suffix). */
function fmtDatetimeFull(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
  );
}

/** Formats coordinates as `48.900°N 2.450°E`. */
function fmtCoords(lat: number, lng: number): string {
  const la = `${Math.abs(lat).toFixed(3)}°${lat >= 0 ? 'N' : 'S'}`;
  const lo = `${Math.abs(lng).toFixed(3)}°${lng >= 0 ? 'E' : 'W'}`;
  return `${la} ${lo}`;
}

// ── FlightLayers sub-component ────────────────────────────────────────────────

interface FlightLayersProps {
  flightId: number;
  onPopup: (info: PopupInfo) => void;
}

/**
 * Renders all map elements for a single selected flight as declarative JSX children
 * of <Map>: trajectory line+points, prediction polygons/heatmaps, team circles,
 * and MarkerBadge-based point markers (start, current position, landing predictions).
 *
 * Visibility is driven by the `layout.visibility` prop on each <Layer> and by
 * conditional rendering of <Marker> elements.
 *
 * @param flightId - ID of the flight to render (its color slot is resolved via
 *                   `flightColorIndex` in the store, stable across reordering)
 * @param onPopup  - called when a landing marker is clicked to show popup
 */
function FlightLayers({ flightId, onPopup }: FlightLayersProps) {
  const flight = useAppStore((s) => s.flights[flightId]);
  const selectedMsgIdx = useAppStore((s) => s.selectedMessageIndex[flightId] ?? null);
  const msgs = useAppStore((s) => s.messages[flightId] ?? []);
  const teams = useAppStore((s) => s.teams[flightId] ?? []);
  const preds = useAppStore((s) => s.predictions[flightId] ?? {});
  const fp = useAppStore((s) => s.flightPrefs[flightId]);
  const prefs = useAppStore((s) => s.prefs);

  if (!flight || !fp) return null;

  const color = getFlightColor(flightId);
  const dk = darkenColor(color);

  const iv = fp.isVisible;
  const sn = fp.showNormal;
  const si = fp.showInstant;
  const st = fp.showTeams;
  const gsp = prefs.globalShowPolygon;
  const gsh = prefs.globalShowHeatmap;

  const vis = (v: boolean): 'visible' | 'none' => (v ? 'visible' : 'none');

  const last = msgs.at(-1);
  const pn = preds.normal;
  const pi = preds.instant;

  return (
    <>
      {/* ── Trajectory line ─────────────────────────────────────────────── */}
      <Source id={`src-tl-${flightId}`} type="geojson" data={buildTrajectoryLine(msgs)}>
        <Layer
          id={`trajectory-line-${flightId}`}
          type="line"
          layout={{
            visibility: vis(iv),
            'line-join': 'round',
            'line-cap': 'round',
          }}
          paint={{
            'line-color': color,
            'line-width': MAP_CONFIG.trajectoryLineWidth,
            'line-opacity': 0.85,
          }}
        />
      </Source>

      {/* ── Trajectory scatter points (clickable) ───────────────────────── */}
      <Source id={`src-tp-${flightId}`} type="geojson" data={buildTrajectoryPoints(msgs)}>
        <Layer
          id={`trajectory-points-${flightId}`}
          type="circle"
          layout={{ visibility: vis(iv) }}
          paint={{
            'circle-radius': [
              'case',
              ['==', ['get', 'msgIdx'], selectedMsgIdx ?? -1],
              MAP_CONFIG.trajectoryPointRadius + 2,   // légèrement plus grand pour l'emphase
              MAP_CONFIG.trajectoryPointRadius,
            ],
            'circle-color': [
              'case',
              ['==', ['get', 'msgIdx'], selectedMsgIdx ?? -1],
              '#ffffff',   // point sélectionné : blanc
              color,       // autres points : couleur du vol
            ],
            'circle-stroke-color': [
              'case',
              ['==', ['get', 'msgIdx'], selectedMsgIdx ?? -1],
              '#111111',   // contour noir sur le point sélectionné
              'white',     // contour blanc sur les autres
            ],
            'circle-stroke-width': [
              'case',
              ['==', ['get', 'msgIdx'], selectedMsgIdx ?? -1],
              2,
              1,
            ],
            'circle-opacity': 0.9,
          }}
        // paint={{
        //   'circle-radius': MAP_CONFIG.trajectoryPointRadius,
        //   'circle-color': color,
        //   'circle-stroke-color': 'white',
        //   'circle-stroke-width': 1,
        //   'circle-opacity': 0.9,
        // }}
        />
      </Source>

      {/* ── Normal prediction polygon ────────────────────────────────────── */}
      {pn?.polygon_points && pn.polygon_points.length >= 3 && (
        <Source id={`src-pnp-${flightId}`} type="geojson" data={buildPolygon(pn.polygon_points)}>
          <Layer
            id={`pred-normal-polygon-${flightId}`}
            type="fill"
            layout={{ visibility: vis(iv && sn && gsp) }}
            paint={{
              'fill-color': hexToRgba(color, 0.6),
              'fill-outline-color': color,
            }}
          />
        </Source>
      )}

      {/* ── Instant prediction polygon ───────────────────────────────────── */}
      {pi?.polygon_points && pi.polygon_points.length >= 3 && (
        <Source id={`src-pip-${flightId}`} type="geojson" data={buildPolygon(pi.polygon_points)}>
          <Layer
            id={`pred-instant-polygon-${flightId}`}
            type="fill"
            layout={{ visibility: vis(iv && si && gsp) }}
            paint={{
              'fill-color': hexToRgba(dk, 0.6),
              'fill-outline-color': dk,
            }}
          />
        </Source>
      )}

      {/* ── Normal prediction heatmap ────────────────────────────────────── */}
      {pn?.heatmap_points && pn.heatmap_points.length > 0 && (
        <Source id={`src-pnh-${flightId}`} type="geojson" data={buildHeatmap(pn.heatmap_points)}>
          <Layer
            id={`pred-normal-heatmap-${flightId}`}
            type="heatmap"
            layout={{ visibility: vis(iv && sn && gsh) }}
            paint={{
              'heatmap-opacity': HEATMAP_CONFIG.opacity,
              'heatmap-radius': HEATMAP_CONFIG.radius,
              'heatmap-intensity': HEATMAP_CONFIG.intensity,
              'heatmap-color': heatmapColorExpr(color) as ExpressionSpecification,
            }}
          />
        </Source>
      )}

      {/* ── Instant prediction heatmap ───────────────────────────────────── */}
      {pi?.heatmap_points && pi.heatmap_points.length > 0 && (
        <Source id={`src-pih-${flightId}`} type="geojson" data={buildHeatmap(pi.heatmap_points)}>
          <Layer
            id={`pred-instant-heatmap-${flightId}`}
            type="heatmap"
            layout={{ visibility: vis(iv && si && gsh) }}
            paint={{
              'heatmap-opacity': HEATMAP_CONFIG.opacity,
              'heatmap-radius': HEATMAP_CONFIG.radius,
              'heatmap-intensity': HEATMAP_CONFIG.intensity,
              'heatmap-color': heatmapColorExpr(dk) as ExpressionSpecification,
            }}
          />
        </Source>
      )}

      {/* ── Team positions — one <MarkerBadge> per terminal with valid coords ── */}
      {iv && st && teams
        .filter((t) => t.longitude != null && t.latitude != null)
        .map((t) => (
          <MarkerBadge
            key={t.terminal_name}
            longitude={t.longitude!}
            latitude={t.latitude!}
            anchor="center"
            color={color}
            Icon={IconMapping.marker_team}
            onClick={() =>
              onPopup({
                kind: 'team',
                lng: t.longitude!,
                lat: t.latitude!,
                terminalName: t.terminal_name,
                teamName: t.team_name,
                userName: t.user_name,
                timestamp: t.timestamp,
              })
            }
          />
        ))
      }

      {/* ── Start marker — Target icon in colored badge.
           Use the first *message* coordinate when available (most accurate);
           fall back to flight departure coords only if no messages loaded yet. ── */}
      {iv && (
        <MarkerBadge
          longitude={msgs[0]?.longitude ?? flight.longitude}
          latitude={msgs[0]?.latitude ?? flight.latitude}
          anchor="center"
          color={color}
          Icon={IconMapping.marker_start}
        />
      )}

      {/* ── Current position marker — MapPin in colored badge, anchored bottom ── */}
      {iv && last && (
        <MarkerBadge
          longitude={last.longitude}
          latitude={last.latitude}
          anchor="bottom"
          color={color}
          Icon={IconMapping.marker_current}
          onClick={() => {
            const store = useAppStore.getState();
            store.setSelectedMessageIndex(flightId, null);
            store.setPanelOpen(true);
            store.setActivePanelFlight(flightId);
          }}
        />
      )}

      {/* ── Normal landing marker — Flag in colored badge ────────────────── */}
      {iv && sn && pn && (
        <MarkerBadge
          longitude={pn.longitude}
          latitude={pn.latitude}
          anchor="center"
          color={color}
          Icon={IconMapping.marker_landing}
          onClick={() =>
            onPopup({
              kind: 'landing',
              lng: pn.longitude,
              lat: pn.latitude,
              predType: 'Prédiction normale',
              predLat: pn.latitude,
              predLng: pn.longitude,
              city: pn.city,
              distKm: pn.city_distance_km,
              landingTime: pn.landing_time,
            })
          }
        />
      )}

      {/* ── Instant landing marker — Flag in darker badge ────────────────── */}
      {iv && si && pi && (
        <MarkerBadge
          longitude={pi.longitude}
          latitude={pi.latitude}
          anchor="center"
          color={dk}
          Icon={IconMapping.marker_landing}
          onClick={() =>
            onPopup({
              kind: 'landing',
              lng: pi.longitude,
              lat: pi.latitude,
              predType: 'Prédiction instantanée',
              predLat: pi.latitude,
              predLng: pi.longitude,
              city: pi.city,
              distKm: pi.city_distance_km,
              landingTime: pi.landing_time,
            })
          }
        />
      )}
    </>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface MapProps {
  mapRef: MutableRefObject<MapRef | null>;
  /** Optional saved viewport from sessionStorage (§23b). Falls back to MAP_CONFIG.initialViewState. */
  initialViewport?: MapViewport | null;
  onStyleLoad?: () => void;
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Full-viewport map component built on react-map-gl/maplibre.
 * Style switching is driven by the `mapStyle` prop — no imperative setStyle() calls.
 * All flight data layers are JSX children auto-synced by react-map-gl across style changes.
 */
export default function Map({ mapRef, initialViewport, onStyleLoad }: MapProps) {
  const prefs = useAppStore((s) => s.prefs);
  const setPrefs = useAppStore((s) => s.setPrefs);
  const selectedFlightIds = useAppStore((s) => s.selectedFlightIds);
  const flights = useAppStore((s) => s.flights);
  const messages = useAppStore((s) => s.messages);
  const predictions = useAppStore((s) => s.predictions);
  const flightPrefs = useAppStore((s) => s.flightPrefs);
  const teams = useAppStore((s) => s.teams);
  const isPanelOpen = useAppStore((s) => s.isPanelOpen);

  // ── Style state ─────────────────────────────────────────────────────────────

  const [styleKey, setStyleKey] = useState<MapStyleKey>(prefs.defaultMapStyle);
  const hybridCacheRef = useRef<object | null>(null);

  // 'satellite' = hybrid (ArcGIS + labels) — needs async build; start as null.
  // 'street' = synchronous URL string; initialize immediately.
  const [resolvedStyle, setResolvedStyle] = useState<string | object | null>(() => {
    const key = prefs.defaultMapStyle;
    if (key === 'satellite') return null;
    return MAP_STYLES[key].styleUrl as string | object;
  });

  useEffect(() => {
    if (resolvedStyle !== null) return;
    void buildHybridStyle().then((style) => {
      hybridCacheRef.current = style;
      setResolvedStyle(style);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveStyle = useCallback(async (key: MapStyleKey): Promise<string | object> => {
    if (key === 'satellite') {
      if (!hybridCacheRef.current) {
        hybridCacheRef.current = await buildHybridStyle();
      }
      return hybridCacheRef.current;
    }
    return MAP_STYLES[key].styleUrl as string | object;
  }, []);

  // ── Style switching ─────────────────────────────────────────────────────────

  /**
   * Switches the map base style. Keeps the current style visible until the
   * new one is fully resolved (avoids blank map during hybrid fetch).
   * @param key - target style key
   */
  const switchStyle = useCallback(
    async (key: MapStyleKey) => {
      if (key === styleKey) return;
      setStyleKey(key);
      setPrefs({ defaultMapStyle: key });
      const style = await resolveStyle(key);
      setResolvedStyle(style);
    },
    [styleKey, setPrefs, resolveStyle],
  );

  // ── Session restore — save viewport on unload ────────────────────────────────

  useEffect(() => {
    const handleUnload = () => {
      if (mapRef.current) saveViewport(mapRef.current);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [mapRef]);

  // ── Popup state ─────────────────────────────────────────────────────────────

  const [popup, setPopup] = useState<PopupInfo | null>(null);

  // ── Click interactions ──────────────────────────────────────────────────────

  /**
   * Unified onClick for trajectory-points and teams layers (interactiveLayerIds).
   * Dispatches on feature.layer.id to the correct handler.
   */
  const handleMapClick = useCallback(
    (event: MapLayerMouseEvent) => {
      const features = event.features;
      if (!features || features.length === 0) return;

      const feature = features[0];
      const layerId = feature.layer.id;

      const trajMatch = /^trajectory-points-(\d+)$/.exec(layerId);
      if (trajMatch) {
        const fid = parseInt(trajMatch[1], 10);
        const props = feature.properties as { msgIdx?: number } | null;
        if (props?.msgIdx !== undefined) {
          const store = useAppStore.getState();
          store.setSelectedMessageIndex(fid, props.msgIdx);
          store.setPanelOpen(true);
          store.setActivePanelFlight(fid);
        }
        return;
      }

      // Team markers use <MarkerBadge> with a React onClick — not interactiveLayerIds.
    },
    [],
  );

  /** Cursor: pointer when over interactive layers, grab otherwise. */
  const handleMouseMove = useCallback((event: MapLayerMouseEvent) => {
    const canvas = mapRef.current?.getCanvas();
    if (canvas) {
      canvas.style.cursor = (event.features && event.features.length > 0)
        ? 'pointer'
        : 'grab';
    }
  }, [mapRef]);

  // ── interactiveLayerIds ─────────────────────────────────────────────────────

  // Only trajectory-points layers are interactive via interactiveLayerIds.
  // Team markers now use <MarkerBadge> with a React onClick instead.
  const interactiveLayerIds = useMemo(
    () => selectedFlightIds.map((id) => `trajectory-points-${id}`),
    [selectedFlightIds],
  );

  // ── Fit-all ─────────────────────────────────────────────────────────────────

  const handleFitAll = useCallback(() => {
    fitToSelection(
      Object.values(flights), flightPrefs, messages, teams,
      predictions, isPanelOpen, prefs.panelWidth, mapRef,
    );
  }, [flights, flightPrefs, messages, teams, predictions, isPanelOpen, prefs.panelWidth, mapRef]);

  // ── initialViewState ────────────────────────────────────────────────────────

  const initViewState = initialViewport
    ? {
      longitude: initialViewport.center[0],
      latitude: initialViewport.center[1],
      zoom: initialViewport.zoom,
      pitch: initialViewport.pitch,
      bearing: initialViewport.bearing,
    }
    : MAP_CONFIG.initialViewState;

  // ── Icons ───────────────────────────────────────────────────────────────────

  const FitAllIcon = IconMapping.map_fit_all;
  const ExternalLinkIcon = IconMapping.prediction_gmaps;
  const styleIconMap: Record<MapStyleKey, typeof FitAllIcon> = {
    street: IconMapping.map_style_street,
    satellite: IconMapping.map_style_satellite,
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={styles.wrapper}>
      {resolvedStyle && (
        <RMap
          ref={mapRef}
          mapStyle={resolvedStyle as string}
          initialViewState={initViewState}
          style={{ width: '100%', height: '100%' }}
          attributionControl={{ compact: true }}
          interactiveLayerIds={interactiveLayerIds}
          onClick={handleMapClick}
          onMouseMove={handleMouseMove}
          onLoad={() => onStyleLoad?.()}
        >
          {/* ── Controls ─────────────────────────────────────────────────── */}
          <NavigationControl position="top-left" showCompass />
          <ScaleControl position="bottom-left" unit="metric" />

          {/* ── Per-flight layers and markers ────────────────────────────── */}
          {selectedFlightIds.map((id) => (
            <FlightLayers
              key={id}
              flightId={id}
              onPopup={setPopup}
            />
          ))}

          {/* ── Popup ────────────────────────────────────────────────────── */}
          {popup && (
            <Popup
              longitude={popup.lng}
              latitude={popup.lat}
              closeButton={false}
              closeOnClick={false}
              className="omen-popup"
              onClose={() => setPopup(null)}
            >
              <div
                style={{ fontSize: '0.75rem', lineHeight: 1.6, color: '#eee', minWidth: 160, cursor: 'pointer' }}
                onClick={() => setPopup(null)}
              >
                {popup.kind === 'team' ? (
                  <>
                    {/* Line 1: "{team_name} ({terminal_name})", fallback: terminal_name only */}
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      {popup.teamName ? `${popup.teamName} (${popup.terminalName})` : popup.terminalName}
                    </div>
                    {/* Line 2: "From {user_name}" — omitted if null */}
                    {popup.userName && (
                      <div style={{ color: '#ccc', marginBottom: 2 }}>From {popup.userName}</div>
                    )}
                    {/* Line 3: full UTC timestamp YYYY-MM-DD HH:MM:SS */}
                    <div style={{ color: '#aaa' }}>{fmtDatetimeFull(popup.timestamp)}</div>
                    <div style={{ marginTop: 6, fontSize: '0.6875rem', color: '#888' }}>Cliquez pour fermer</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{popup.predType}</div>
                    <a
                      href={`https://maps.google.com/?q=${popup.predLat},${popup.predLng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#6ab0f5', textDecoration: 'underline' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {fmtCoords(popup.predLat, popup.predLng)}&nbsp;
                      <ExternalLinkIcon size={11} />
                    </a>
                    <div>{popup.city} ({popup.distKm.toFixed(1)}&nbsp;km)</div>
                    <div>{fmtTime(popup.landingTime)}</div>
                    <div style={{ marginTop: 6, fontSize: '0.6875rem', color: '#888' }}>Cliquez pour fermer</div>
                  </>
                )}
              </div>
            </Popup>
          )}
        </RMap>
      )}

      {/* ── Fit-all button — top-left, below NavigationControl ─────────── */}
      <div className={styles.fitAllWrap}>
        <button
          className={styles.fitAllBtn}
          onClick={handleFitAll}
          title="Ajuster la vue à tous les vols"
          aria-label="Ajuster la vue à tous les vols"
          data-tour="tour-map-fit-all"
        >
          <FitAllIcon size={15} />
        </button>
      </div>

      {/* ── Style switcher — bottom-left ─────────────────────────────────── */}
      <div className={styles.styleSwitcher} data-tour="tour-map-basemap">
        {MAP_STYLE_CYCLE.map((key) => {
          const StyleIcon = styleIconMap[key];
          const isActive = key === styleKey;
          return (
            <button
              key={key}
              className={`${styles.styleBtn} ${isActive ? styles.styleBtnActive : ''}`}
              onClick={() => void switchStyle(key)}
              title={MAP_STYLES[key].label}
              aria-label={MAP_STYLES[key].label}
              aria-pressed={isActive}
            >
              <StyleIcon size={14} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
